import { useMemo } from "react";
import { INestedMessages, flattenMessages } from "./flattenMessages.ts";
import { useSearchParams } from "react-router";
import English from "../i18n/messages/en.json";
import Hindi from "../i18n/messages/hi.json";
import Nepali from "../i18n/messages/ne.json";
import Gujarati from "../i18n/messages/gu.json";
import Malayalam from "../i18n/messages/ml.json";
import Tamil from "../i18n/messages/ta.json";
import Punjati from "../i18n/messages/pa.json";
import Marathi from "../i18n/messages/mr.json";
import Bengali from "../i18n/messages/bn.json";
import Oriya from "../i18n/messages/or.json";
import Spanish from "../i18n/messages/es.json";
import Russian from "../i18n/messages/ru.json";

export type Locale =
  | "en"
  | "es"
  | "hi"
  | "ne"
  | "gu"
  | "ml"
  | "ta"
  | "pa"
  | "ru"
  | "mr"
  | "bn"
  | "or";

// a Record is an object wich we can pass union types to it as key.
const messages: Record<Locale, INestedMessages> = {
  en: English,
  es: Spanish,
  hi: Hindi,
  ne: Nepali,
  gu: Gujarati,
  ml: Malayalam,
  ta: Tamil,
  pa: Punjati,
  ru: Russian,
  mr: Marathi,
  bn: Bengali,
  or: Oriya,
};

export const useLocale = () => {
  const [params] = useSearchParams();
  const paramLocale = (params.get("lang") || "en") as Locale;
  const defaultMessages = useMemo(() => flattenMessages(English), []);
  const flattenedMessages = useMemo(
    () => ({
      ...defaultMessages,
      ...flattenMessages(messages[paramLocale as keyof typeof messages] || {}),
    }),
    [paramLocale, defaultMessages]
  );
  return {
    locale: paramLocale,
    messages: flattenedMessages,
  };
};

// Define a TypeScript type called KeyPaths that takes in an object type T as its generic type parameter.
// KeyPaths is defined as a mapped type, which produces a new type by iterating over the keys of T.
type KeyPaths<T extends INestedMessages> = {
  // For each key K in T, create a mapped type that checks whether the value of that key T[K] extends INestedMessages,
  // i.e., whether the value is an object that has string keys and unknown values.
  [K in keyof T]: T[K] extends INestedMessages
    ? // If the value of the key is an object, create a string literal type that represents the path to that object.
      // The path is constructed by combining the current key K with a dot separator and the key paths of the nested object T[K].
      // The & string is used to ensure that TypeScript recognizes this string literal as a string.

      // @ts-ignore
      `${K & string}.${KeyPaths<T[K]> & string}`
    : // If the value of the key is not an object, simply return the key name K.
      K;
  // Finally, index the KeyPaths type by keyof T, which returns a union of all the key paths in the object type T.
  // This means that KeyPaths<T> is a union of all the possible key paths in the object type T.
}[keyof T];

export type TranslationKey = KeyPaths<typeof English>;
